import { useGoogleMap } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Replaces `@react-google-maps`' `<InfoWindow>`, which throws an invariant from a commit
 * callback whenever the Maps instance reports no position right after `setPosition()` — seen
 * in production while editing circle zones, and it takes the whole delivery settings page down.
 * This one opens the window once Maps reports a position instead of throwing.
 */
export default function DeliveryZoneInfoWindow({ position, children }) {
  const map = useGoogleMap();
  const [container] = useState(() => document.createElement('div'));
  const infoWindowRef = useRef(null);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const infoWindow = new window.google.maps.InfoWindow({ content: container });
    let isOpen = false;

    const openWhenPositioned = () => {
      if (isOpen || !infoWindow.getPosition()) {
        return;
      }

      infoWindow.open({ map, shouldFocus: false });
      isOpen = true;
    };

    const listener = infoWindow.addListener('position_changed', openWhenPositioned);
    infoWindowRef.current = infoWindow;

    return () => {
      listener.remove();
      infoWindow.close();
      infoWindowRef.current = null;
    };
  }, [map, container]);

  useEffect(() => {
    if (position) {
      infoWindowRef.current?.setPosition(position);
    }
  }, [map, position]);

  return createPortal(children, container);
}
